import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';

import { DictionaryItem, getQueryParams } from '@hglobal/fin-ui';
import dayjs from 'dayjs';
import { map, Observable } from 'rxjs';

import { environment } from '@kr/environment';
import { OrganizationService } from '@kr/services';
import { DictionariesStore } from '@kr/store';

import { GetFirstTransactionDateRes, GetTransactionsReq, GetTransactionsRes, SaveCommentReq, SaveCommentRes } from '../models';
import { TransactionsApi } from './transactions-api.interface';

@Injectable()
export class TransactionsApiService implements TransactionsApi {
  readonly #http = inject(HttpClient);
  readonly #dictionariesStore = inject(DictionariesStore);
  readonly #organizationService = inject(OrganizationService);
  readonly #apiHost = environment.apiHost;

  getTransactions(params: GetTransactionsReq, providerId: number): Observable<GetTransactionsRes> {
    const api = `${this.#apiHost}/transactions/`;
    const ids = providerId || this.#dictionariesStore.providersIds();
    return this.#fetchTransactions(api, params, ids);
  }

  saveComment(params: SaveCommentReq): Observable<SaveCommentRes> {
    const api = `${this.#apiHost}/transactions/comment/modify/`;
    const { transactionId, comment } = params;

    return this.#http.put<SaveCommentRes>(
      api,
      { transactionId, comment },
      { headers: this.#organizationService.getHttpHeaders(params.internalOrgId) },
    );
  }

  getMonthlyRepYears(): Observable<DictionaryItem<string>[]> {
    const api = `${this.#apiHost}/transactions/info`;

    return this.#http
      .get<GetFirstTransactionDateRes>(api, {
        headers: this.#organizationService.getHttpHeaders(),
      })
      .pipe(map(date => this.#getMonthlyReportYearsList(date.first_transaction_date)));
  }

  #fetchTransactions(api: string, params: GetTransactionsReq, providerIds: number | number[]): Observable<GetTransactionsRes> {
    return this.#http
      .get<GetTransactionsRes>(api, {
        params: getQueryParams(params, ['providerId'], true),
        headers: this.#organizationService.getHttpHeaders(providerIds),
      })
      .pipe(
        map(data => ({
          itemsCount: data.itemsCount,
          transactions: data.transactions.map(item => {
            item.created = item.created * 1000;
            return item;
          }),
        })),
      );
  }

  #getMonthlyReportYearsList(unixDate: number): DictionaryItem<string>[] {
    const startYear = dayjs.unix(unixDate).year();
    const currentYear = dayjs().year();

    return Array.from({ length: currentYear - startYear + 1 }, (_, index) => {
      const year = (startYear + index).toString();
      return { id: year, name: year };
    });
  }
}
